import classNames from "classnames";
import { Link } from "react-router-dom";
import { common_utils } from "../../../../../../common/common_utils";
import {
	getRawProductWithoutPrices,
	PaymentInterval,
	PriceDescriptor,
} from "../../../../../../common/premium_tiers";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { Tag } from "../base_components/tag";
import { InfoTooltip } from "../tooltips/info_tooltip";
import { centsToDollars } from "./pricing_table";
import { isFeatureEnabled } from "../../../../../../src/feature_flags";

type Coupon =
	RouterInferenceHelpers["premium"]["getAllProductsPrices"]["output"]["active_coupon"];

type BasePricingTierProps = {
	link_type: "anchor" | "Link";
	is_best_deal?: boolean;
	title: string;
	features: (string | JSX.Element)[];
	tag_type?: "Best deal" | "Exclusive";
	is_glowing?: boolean;
};

type RegularPricingTierProps = BasePricingTierProps & {
	type: "regular";
	span: PaymentInterval;
	has_used_free_trial?: boolean;
	product_id: string;
	price?: PriceDescriptor;
	active_coupon?: Coupon;
	onPurchaseClick?: () => void; // Optional click handler to intercept purchase
	buttonText?: string; // Optional custom button text
};

type CustomContactPricingTierProps = BasePricingTierProps & {
	type: "custom-contact";
	onContactClick?: () => void;
	buttonText?: string; // Optional custom button text (defaults to "Contact Us")
};

type CustomCheckoutPricingTierProps = BasePricingTierProps & {
	type: "custom";
	span: PaymentInterval;
	has_used_free_trial?: boolean;
	product_id: string;
	guild_id: string;
	price: PriceDescriptor;
	active_coupon?: Coupon | null;
};

export type PricingTierProps =
	| RegularPricingTierProps
	| CustomContactPricingTierProps
	| CustomCheckoutPricingTierProps;

// Helper to check if a coupon applies to a specific product
function couponAppliesToProduct(coupon: Coupon | undefined | null, productId: string): boolean {
	if (!coupon) return false;

	// Check our stored applies_to_products field
	if (coupon.applies_to_products && coupon.applies_to_products.length > 0) {
		return coupon.applies_to_products.includes(productId);
	}

	// If no applies_to specified, coupon applies to all products (legacy behavior)
	return true;
}

// Helper to format discount text
function getDiscountText(coupon: Coupon): string {
	if (coupon.api_data.percent_off) {
		return `${coupon.api_data.percent_off}% off`;
	}
	if (coupon.api_data.amount_off) {
		return `$${(coupon.api_data.amount_off / 100).toFixed(0)} off`;
	}
	return "Sale";
}

export function PricingTier(props: PricingTierProps) {
	const is_best_deal = props.is_best_deal ?? false;

	// Check if coupon applies to this product
	const couponApplies = props.type === "regular" &&
		couponAppliesToProduct(props.active_coupon, props.product_id);

	return (
		<div
			className={classNames(
				"flex w-full max-w-[17rem] flex-shrink-0 flex-col gap-4 rounded-lg border-2 px-4 pb-8 pt-4 shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl sm:max-w-[20rem] sm:whitespace-nowrap",
				{
					"border-sesh-primary bg-gradient-to-b from-base-light via-base-light to-base-dark":
						is_best_deal && !props.is_glowing,
					"border-content-secondary bg-gradient-to-b from-base-light to-base-light":
						!is_best_deal && !props.is_glowing,
					"border-sesh-primary bg-gradient-to-b from-cyan-950/40 via-base-light to-purple-950/30 ring-4 ring-sesh-primary/50 ring-offset-2 ring-offset-base-dark":
						props.is_glowing,
				}
			)}
		>
			<div className="flex min-h-[2rem] w-full">
				{couponApplies && props.active_coupon && (
					<Tag type="Light" className="font-semibold">
						{getDiscountText(props.active_coupon)}
					</Tag>
				)}
				{props.tag_type && (
					<Tag
						type="Dark"
						className={classNames("ml-auto", {
							"animate-pulse bg-premium font-bold !text-base-dark":
								props.tag_type === "Best deal",
							"animate-pulse bg-sesh-primary px-2 py-1 text-sm font-bold !text-base-dark":
								props.tag_type === "Exclusive",
						})}
					>
						{props.tag_type}
					</Tag>
				)}
			</div>
			<div className="mx-2 flex h-full flex-col">
				<div
					className={classNames(
						"mb-1 flex h-[4rem] flex-col items-center justify-center text-center"
					)}
				>
					{props.title === "Premium Bundle" ? (
						<div className="flex flex-col items-center">
							<i
								className={classNames(
									"fas fa-gem text-sm sm:text-base",
									{
										"text-sesh-primary": true,
										"text-content-primary": false,
									}
								)}
							></i>
							<div className="-mt-0.5 flex gap-1">
								<i
									className={classNames(
										"fas fa-gem text-sm sm:text-base",
										{
											"text-sesh-primary": true,
											"text-content-primary": false,
										}
									)}
								></i>
								<i
									className={classNames(
										"fas fa-gem text-sm sm:text-base",
										{
											"text-sesh-primary": true,
											"text-content-primary": false,
										}
									)}
								></i>
							</div>
						</div>
					) : props.title === "sesh Pro" ||
					  props.type === "custom" ||
					  props.type === "custom-contact" ? (
						<i className="fas fa-crown text-xl text-amber-300 sm:text-2xl"></i>
					) : (
						<i className="fas fa-gem text-xl text-sesh-primary sm:text-2xl"></i>
					)}
					{props.title === "sesh Pro" ? (
						<div className="mt-1 bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 bg-clip-text text-xl font-extrabold text-transparent sm:text-2xl">
							{props.title}
						</div>
					) : (
						<div
							className={classNames(
								"mt-1 text-xl font-extrabold sm:text-2xl",
								{
									"text-sesh-primary":
										props.title === "Premium Bundle",
									"text-content-primary":
										props.title !== "Premium Bundle",
								}
							)}
						>
							{props.title}
						</div>
					)}
				</div>
				<div
					className={classNames("mb-2 border-b pb-1", {
						"border-sesh-primary": is_best_deal,
						"border-content-secondary": !is_best_deal,
					})}
				>
					{props.type === "regular" && props.price && (
						<FullPriceDisplay
							price={props.price}
							span={props.span}
							coupon={couponApplies ? props.active_coupon : undefined}
							is_best_deal={is_best_deal}
							type="regular"
							is_sesh_pro={props.title === "sesh Pro"}
						/>
					)}
					{props.type === "custom" && (
						<FullPriceDisplay
							price={props.price}
							span={props.span}
							is_best_deal={is_best_deal}
							type="custom"
						/>
					)}
					{props.type === "custom-contact" && <ContactPriceDisplay />}
				</div>
				<div className="flex-grow text-sm sm:text-base">
					<ul className="min-h-[16rem]">
						{props.features.map((feature, index) => (
							<li
								key={index}
								className="mt-2 flex items-start gap-2"
							>
								<i
									className={classNames(
										"fas fa-check-circle mt-0.5 text-lg",
										{
											"text-success-primary":
												is_best_deal,
											"text-content-primary":
												!is_best_deal,
										}
									)}
									title="Active"
									suppressHydrationWarning={true}
								></i>{" "}
								<span className="text-content-primary">
									{feature}
								</span>
							</li>
						))}
					</ul>
				</div>
				{props.type === "regular" ? (
					<>
						<div className="mt-auto pt-8">
							{props.onPurchaseClick ? (
								<SeshPrimaryButton
									onClick={props.onPurchaseClick}
									className={classNames(
										"justify-center font-extrabold",
										{
											"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
												is_best_deal,
											"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
												!is_best_deal,
										}
									)}
								>
									{props.buttonText ??
										(props.span === PaymentInterval.OneTime
											? "Get Access Now"
											: props.has_used_free_trial ||
											  !isFeatureEnabled("FreeTrial")
											? "Subscribe Now"
											: "Start Free Trial")}
								</SeshPrimaryButton>
							) : props.link_type === "Link" ? (
								<Link
									to={common_utils.getStripeRedirectURL({
										type: "checkout",
										product_id: props.product_id,
										subscription_span:
											props.span.toString(),
										should_force_no_coupon: "false",
									})}
									className="mt-2"
								>
									<SeshPrimaryButton
										className={classNames(
											"justify-center font-extrabold",
											{
												"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
													is_best_deal,
												"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
													!is_best_deal,
											}
										)}
									>
										{props.span === PaymentInterval.OneTime
											? "Get Access Now"
											: props.has_used_free_trial ||
											  !isFeatureEnabled("FreeTrial")
											? "Subscribe Now"
											: "Start Free Trial"}
									</SeshPrimaryButton>
								</Link>
							) : (
								<a
									href={common_utils.getStripeRedirectURL({
										type: "checkout",
										product_id: props.product_id,
										subscription_span:
											props.span.toString(),
										should_force_no_coupon: "false",
									})}
									className="mt-2"
								>
									<SeshPrimaryButton
										className={classNames(
											"justify-center font-extrabold",
											{
												"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
													is_best_deal,
												"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
													!is_best_deal,
											}
										)}
									>
										{props.span === PaymentInterval.OneTime
											? "Get Access Now"
											: props.has_used_free_trial ||
											  !isFeatureEnabled("FreeTrial")
											? "Subscribe Now"
											: "Start Free Trial"}
									</SeshPrimaryButton>
								</a>
							)}
						</div>
					</>
				) : props.type === "custom-contact" ? (
					<>
						<div className="mt-auto pt-8">
							{props.onContactClick ? (
								<SeshPrimaryButton
									onClick={props.onContactClick}
									className={classNames(
										"justify-center font-extrabold",
										{
											"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
												is_best_deal,
											"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
												!is_best_deal,
										}
									)}
								>
									{props.buttonText ?? "Contact Us"}
								</SeshPrimaryButton>
							) : props.link_type === "Link" ? (
								<Link to="/dashboard/contact-us">
									<SeshPrimaryButton
										className={classNames(
											"justify-center font-extrabold",
											{
												"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
													is_best_deal,
												"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
													!is_best_deal,
											}
										)}
									>
										{props.buttonText ?? "Contact Us"}
									</SeshPrimaryButton>
								</Link>
							) : (
								<a href="/dashboard/contact-us">
									<SeshPrimaryButton
										className={classNames(
											"justify-center font-extrabold",
											{
												"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
													is_best_deal,
												"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
													!is_best_deal,
											}
										)}
									>
										{props.buttonText ?? "Contact Us"}
									</SeshPrimaryButton>
								</a>
							)}
						</div>
					</>
				) : (
					<>
						<div className="mt-auto pt-8">
							{props.link_type === "Link" ? (
								<Link
									to={common_utils.getStripeRedirectURL({
										type: "checkout",
										product_id: props.product_id,
										guild_id: props.guild_id,
										subscription_span:
											props.span.toString(),
										should_force_no_coupon: "true",
									})}
									className="mt-2"
								>
									<SeshPrimaryButton
										className={classNames(
											"justify-center font-extrabold",
											{
												"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-cyan-300":
													props.is_glowing,
												"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
													is_best_deal &&
													!props.is_glowing,
												"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
													!is_best_deal &&
													!props.is_glowing,
											}
										)}
									>
										Get sesh Pro
									</SeshPrimaryButton>
								</Link>
							) : (
								<a
									href={common_utils.getStripeRedirectURL({
										type: "checkout",
										product_id: props.product_id,
										guild_id: props.guild_id,
										subscription_span:
											props.span.toString(),
										should_force_no_coupon: "true",
									})}
									className="mt-2"
								>
									<SeshPrimaryButton
										className={classNames(
											"justify-center font-extrabold",
											{
												"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-cyan-300":
													props.is_glowing,
												"bg-sesh-primary !text-base-dark transition-all duration-300 hover:bg-opacity-90 hover:!text-white":
													is_best_deal &&
													!props.is_glowing,
												"border-2 border-content-primary !text-content-primary transition-all duration-300 hover:bg-content-primary hover:!text-base-dark":
													!is_best_deal &&
													!props.is_glowing,
											}
										)}
									>
										Get sesh Pro
									</SeshPrimaryButton>
								</a>
							)}
						</div>
					</>
				)}
			</div>
		</div>
	);
}

function FullPriceDisplay(props: {
	price: PriceDescriptor;
	coupon?: Coupon;
	span: PaymentInterval;
	is_best_deal?: boolean;
	type?: "regular" | "custom";
	is_sesh_pro?: boolean;
}) {
	const { price, coupon, span, is_best_deal, type, is_sesh_pro } = props;

	// Calculate discounted price based on coupon type
	let discountedCents: number | null = null;
	if (coupon) {
		if (coupon.api_data.percent_off) {
			discountedCents = price.cents * (1 - coupon.api_data.percent_off / 100);
		} else if (coupon.api_data.amount_off) {
			// amount_off is in cents
			discountedCents = Math.max(0, price.cents - coupon.api_data.amount_off);
		}
	}

	// For annual plans, calculate monthly cost
	const displayCents = span === PaymentInterval.Year ? price.cents / 12 : price.cents;
	const displayDiscountedCents = discountedCents !== null
		? (span === PaymentInterval.Year ? discountedCents / 12 : discountedCents)
		: null;

	// If there's a coupon, show both prices inline
	if (coupon && displayDiscountedCents !== null) {
		return (
			<div className="mb-2 flex flex-col items-center justify-center">
				<div className="flex items-center justify-center gap-2">
					{/* Original price - smaller and crossed out */}
					<span
						className={classNames(
							"text-xl font-normal line-through decoration-1 opacity-50",
							{
								"text-sesh-primary": is_best_deal,
								"text-content-primary": !is_best_deal,
							}
						)}
					>
						${centsToDollars(displayCents)}
					</span>
					{/* Discounted price */}
					<span
						className={classNames("text-4xl font-extrabold", {
							"bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 bg-clip-text text-transparent": is_sesh_pro,
							"text-sesh-primary": is_best_deal && !is_sesh_pro,
							"text-content-primary": !is_best_deal && !is_sesh_pro,
						})}
					>
						${centsToDollars(displayDiscountedCents)}
					</span>
					{span !== PaymentInterval.OneTime && (
						<span
							className={classNames("text-xs font-bold", {
								"bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 bg-clip-text text-transparent": is_sesh_pro,
								"text-sesh-primary": is_best_deal && !is_sesh_pro,
								"text-content-primary": !is_best_deal && !is_sesh_pro,
							})}
						>
							{span === PaymentInterval.Year ? (
								<>
									per
									<br />
									month
								</>
							) : (
								<>
									per
									<br />
									{PaymentInterval[span].toLowerCase()}
								</>
							)}
						</span>
					)}
					{span === PaymentInterval.OneTime && (
						<span
							className={classNames("text-xs font-bold", {
								"bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 bg-clip-text text-transparent": is_sesh_pro,
								"text-sesh-primary": is_best_deal && !is_sesh_pro,
								"text-content-primary": !is_best_deal && !is_sesh_pro,
							})}
						>
							one
							<br />
							time
						</span>
					)}
				</div>
				<div
					className={classNames("text-xs", {
						invisible: span !== PaymentInterval.Year || type === "custom",
						"text-content-secondary":
							span === PaymentInterval.Year && type !== "custom",
					})}
					aria-hidden={span !== PaymentInterval.Year || type === "custom"}
				>
					Billed Annually
				</div>
			</div>
		);
	}

	// No coupon - show regular price display
	return (
		<PriceDisplay
			cents={price.cents}
			span={span}
			is_best_deal={is_best_deal}
			type={type}
		/>
	);
}

function PriceDisplay(props: {
	cents: number;
	span: PaymentInterval;
	striked_through?: boolean;
	is_best_deal?: boolean;
	is_discounted?: boolean;
	type?: "regular" | "custom";
}) {
	const { cents, span, type } = props;

	// For annual plans, calculate and show monthly cost
	const displayCents = span === PaymentInterval.Year ? cents / 12 : cents;

	return (
		<div
			className={classNames(
				"mb-2 flex flex-col items-center justify-center",
				{
					"opacity-25": props.striked_through,
					"text-sesh-primary":
						props.is_best_deal && !props.striked_through,
					"text-content-primary":
						!props.is_best_deal && !props.striked_through,
				}
			)}
		>
			<div className="flex items-center justify-center gap-1">
				{type === "custom" ? (
					<div className="text-4xl font-extrabold text-content-primary">
						Contact Us
					</div>
				) : (
					<>
						<div
							className={classNames("text-4xl font-extrabold", {
								"font-normal line-through decoration-2":
									props.striked_through,
							})}
						>
							${centsToDollars(displayCents)}
						</div>
						{(span !== PaymentInterval.OneTime && (
							<div
								className={classNames("text-xs font-bold", {
									"line-through": props.striked_through,
								})}
							>
								{span === PaymentInterval.Year ? (
									<>
										per
										<br />
										month
									</>
								) : (
									<>
										per
										<br />
										{PaymentInterval[span].toLowerCase()}
									</>
								)}
							</div>
						)) || (
							<div
								className={classNames("text-xs font-bold", {
									"line-through": props.striked_through,
								})}
							>
								one
								<br />
								time
							</div>
						)}
					</>
				)}
			</div>
			<div
				className={classNames("text-xs", {
					invisible:
						span !== PaymentInterval.Year || type === "custom",
					"text-content-secondary":
						span === PaymentInterval.Year && type !== "custom",
				})}
				aria-hidden={span !== PaymentInterval.Year || type === "custom"}
			>
				Billed Annually
			</div>
		</div>
	);
}

function ContactPriceDisplay() {
	return (
		<div className="mb-2 flex flex-col items-center justify-center">
			<div className="flex items-center justify-center gap-1">
				<span
					className="inline-block bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400 bg-clip-text text-3xl font-extrabold text-transparent sm:text-4xl"
					style={{ paddingBottom: "0.1em", marginBottom: "-0.1em" }}
				>
					Contact Us
				</span>
			</div>
			<div className="invisible text-xs">Billed Annually</div>
		</div>
	);
}
